import { Carousel } from "react-bootstrap";
import { AdThumb } from "@/shared/components/AdCard/AdThumb";
import { useResolveAdContent } from "@/shared/hooks/useResolveAdContent";
import type { Ad, ImageContent, SMSContent, VideoContent } from "@models/Ad.model";
import type { ResolvedAdContent } from "@services/media/commands/cmdResolveAds";

interface CollectionCardProps {
    title: string;
    ads: Ad[];
    adCount: number;
    tagCount?: number;
    onClick: () => void;
}

function buildInlineResolved(ad: Ad): ResolvedAdContent | undefined {
    if (ad.type === "container" || !ad.content) return undefined;
    const c = ad.content as VideoContent | ImageContent | SMSContent;
    const media = ("video" in c && c.video) || ("image" in c && c.image) || null;
    const preview = ("thumbnail" in c && c.thumbnail) || null;
    if (!media && !preview) return undefined;
    return { ad, media, preview };
}

function LazyThumbSlide({ ad }: { ad: Ad }) {
    const inline = buildInlineResolved(ad);
    const [ref, fetched] = useResolveAdContent(ad.id, !!inline);
    const resolved = inline ?? fetched;
    return (
        <div ref={ref as any} style={{ width: "100%", aspectRatio: "1 / 1" }}>
            <AdThumb resolved={resolved} />
        </div>
    );
}

export default function CollectionCard({ title, ads, adCount, tagCount, onClick }: CollectionCardProps) {
    const hasMedia = ads.length > 0;
    const stop = (e: React.MouseEvent) => e.stopPropagation();

    return (
        <div className="collection-card">
            <div className="collection-card__head" onClick={onClick}>
                <span className="collection-card__title">{title}</span>
                <span className="collection-card__view">View →</span>
            </div>

            <div className="collection-card__media" onClick={stop}>
                {hasMedia ? (
                    <Carousel interval={null} indicators={ads.length > 1} controls={ads.length > 1}>
                        {ads.map((ad) => (
                            <Carousel.Item key={ad.id}>
                                <LazyThumbSlide ad={ad} />
                            </Carousel.Item>
                        ))}
                    </Carousel>
                ) : (
                    <div className="collection-card__empty-media">No content yet</div>
                )}
            </div>

            <div className="collection-card__stats" onClick={onClick}>
                <div className="stat" role="button">
                    <div className="stat__num">{adCount}</div>
                    <div className="stat__label">Guideposts</div>
                </div>
                {tagCount !== undefined && (
                    <div className="stat" role="button">
                        <div className="stat__num">{tagCount}</div>
                        <div className="stat__label">Principles</div>
                    </div>
                )}
            </div>
        </div>
    );
}
